import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Tag,
  Input,
  Tooltip,
  Space, // Added Space for action layout
  Popconfirm, // Added for delete confirmation
} from "antd";
import { useEffect, useState, useMemo } from "react";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { FileStorageModalAdd } from "./FileStorageModalAdd";
import { ErrorToast, SuccessToast } from "../../ToastComponent";
import moment from "moment";

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "N/A";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export const FileStorage = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const canEdit =
    user?.roles?.includes("FileMovementEditor") &&
    user?.roles?.includes("Operation");

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: "",
    branch: "",
    dateRange: null,
  });

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("user"))?.accessToken}`,
    },
  });

  const availableBranches = useMemo(() => {
    if (!data || data.length === 0) return [];
    const names = data.map((item) => item.branchName).filter(Boolean);
    return [...new Set(names)].sort();
  }, [data]);

  const fetchFileStorageData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/FileMaster?PageNumber=1&PageSize=500&SearchTerm=${searchFilters.searchTerm}`,
        getAuthHeader(),
      );
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to load file storage data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFileStorageData();
  }, [searchFilters.searchTerm]);

  const handleFilterChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  // --- DELETE HANDLER ---
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${Api_Url}api/v1/FileMaster/${id}`, getAuthHeader());
      SuccessToast("Record deleted successfully");
      fetchFileStorageData();
    } catch (error) {
      ErrorToast(error.response?.data?.message || "Failed to delete record");
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (id) => {
    try {
      setLoading(true);
      await axios.put(
        `${Api_Url}api/v1/FileMaster/acknowledge`,
        { fileMasterId: id, remarks: "Received at HO" },
        getAuthHeader(),
      );
      SuccessToast("File acknowledged successfully");
      fetchFileStorageData();
    } catch (error) {
      ErrorToast("Failed to acknowledge file");
    } finally {
      setLoading(false);
    }
  };

  const handleExportToCsv = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/FileMaster/export-file-handover-report`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("user"))?.accessToken}`,
          },
        },
      );
      const fileURL = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute(
        "download",
        `File_Handover_Report_${moment().format("YYYYMMDD")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      ErrorToast("Failed to export report");
    }
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (searchFilters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === searchFilters.branch.toLowerCase(),
      );
    }
    if (searchFilters.dateRange && searchFilters.dateRange.length === 2) {
      const [startDate, endDate] = searchFilters.dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.fileSentDate);
        return itemDate >= startDate.toDate() && itemDate <= endDate.toDate();
      });
    }
    return filtered;
  }, [data, searchFilters]);

  const baseColumns = [
    {
      title: "Branch",
      dataIndex: "branchName",
      key: "branchName",
      width: 130,
    },
    {
      title: "Document Type",
      dataIndex: "documentType",
      key: "documentType",
      width: 130,
      render: (type) => {
        let color = type === "PDD" ? "purple" : "blue";
        return <Tag color={color}>{type || "Main File"}</Tag>;
      },
    },
    {
      title: "PDD Doc Name",
      dataIndex: "pddDocumentName",
      key: "pddDocumentName",
      width: 180,
      render: (text, record) => (record.documentType === "PDD" ? text : "-"),
    },
    {
      title: "Loan Number",
      dataIndex: "loanNumber",
      key: "loanNumber",
      fixed: "left",
      width: 220,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      width: 180,
    },
    {
      title: "Application No",
      dataIndex: "applicationNo",
      key: "applicationNo",
      width: 150,
    },
    {
      title: "First Disbursal Date",
      dataIndex: "firstDisbursalDate",
      render: (date) => formatDate(date),
      width: 150,
    },
    {
      title: "Cheque Handover",
      dataIndex: "chequeHandoverDate",
      render: (date) => formatDate(date),
      width: 150,
    },
    {
      title: "File Sent Date",
      dataIndex: "fileSentDate",
      render: (date) => formatDate(date),
      width: 150,
    },
    {
      title: "POD Number",
      dataIndex: "podNumber",
      render: (text) => text || "N/A",
      width: 130,
    },
    {
      title: "Courier Company",
      dataIndex: "courierCompanyName",
      width: 150,
      render: (text) => text || "N/A",
    },
    {
      title: "Branch Remarks",
      dataIndex: "branchRemarks",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "180px",
            }}
          >
            {text || "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Date",
      dataIndex: "branchOpsSubmitDate",
      render: (date) => formatDate(date),
      width: 120,
    },
    {
      title: "Recieved By HO",
      dataIndex: "isReceivedByHO",
      key: "isReceivedByHO",
      render: (isReceivedByHO, record) => (
        <p className="mb-0">
          {isReceivedByHO ? (
            <span style={{ color: "green", fontWeight: "bold" }}>
              Received (By: {record.hoReceivedBy || "N/A"})
            </span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>
              Not Received
            </span>
          )}
        </p>
      ),
      width: 150,
    },
    {
      title: "Submitted By",
      dataIndex: "branchOpsName",
      render: (name, record) => (
        <Tooltip title={`Code: ${record.branchOpsCode}`}>
          {`${name || "System"} (${record.branchOpsCode || "N/A"})`}
        </Tooltip>
      ),
      width: 180,
    },
    {
      title: "Status",
      dataIndex: "currentStatus",
      render: (status) => (
        <Tag color="blue">{status?.toUpperCase() || "SUBMITTED"}</Tag>
      ),
      width: 150,
    },
  ];

  const columns = canEdit
    ? [
        ...baseColumns,
        {
          title: "Action",
          key: "action",
          fixed: "right",
          width: 160, // Increased width to fit two buttons
          render: (_, record) => (
            <Space size="middle">
              <Button
                type="link"
                onClick={() => setEditingRecord(record)}
                className="p-0"
              >
                <i className="fa-solid fa-pen-to-square"></i> Edit
              </Button>

              {/* Only show delete if NOT received by HO */}
              {!record.isReceivedByHO && (
                <Popconfirm
                  title="Delete Record"
                  description="Are you sure you want to delete this movement record?"
                  onConfirm={() => handleDelete(record.fileMasterId)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" danger className="p-0">
                    <i className="fa-solid fa-trash"></i> Delete
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ),
        },
        {
          title: "HO Receipt",
          dataIndex: "isReceivedByHO",
          key: "hoReceiptAction",
          render: (received, record) => {
            if (received) {
              return (
                <Tooltip
                  title={`Received on ${formatDate(record.hoReceivedDate)}`}
                >
                  <Tag color="success">RECEIVED</Tag>
                </Tooltip>
              );
            }
            return (
              <Button
                type="primary"
                size="small"
                onClick={() => handleAcknowledge(record.fileMasterId)}
              >
                Acknowledge
              </Button>
            );
          },
          width: 150,
        },
      ]
    : baseColumns;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branch File Movement</h3>
            <div className="d-flex align-items-center gap-2">
              <Button
                onClick={handleExportToCsv}
                className="btn btn-success"
                style={{ backgroundColor: "#198754", color: "#fff" }}
              >
                <i className="fa-solid fa-file-excel me-2"></i> Export CSV
              </Button>
              <Button type="primary" onClick={() => setIsModalOpen(true)}>
                <i className="fa-solid fa-plus me-2"></i> Add Handover
              </Button>
            </div>
          </div>
        </div>

        <Card className="my-3">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={searchFilters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                style={{ width: "100%" }}
                showSearch
                size="large"
                disabled={availableBranches.length <= 1}
              >
                {availableBranches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={10}>
              <Search
                placeholder="Search by Loan No / Application No / Name"
                enterButton="Search"
                onSearch={(value) => handleFilterChange("searchTerm", value)}
                allowClear
                size="large"
              />
            </Col>
            <Col xs={24} md={8}>
              <RangePicker
                size="large"
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="fileMasterId"
          loading={loading}
          pagination={{ showSizeChanger: true, defaultPageSize: 10 }}
          scroll={{ x: 1500 }}
        />

        {isModalOpen && (
          <FileStorageModalAdd
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchFileStorageData}
          />
        )}

        {editingRecord && (
          <FileStorageModalAdd
            record={editingRecord}
            onClose={() => setEditingRecord(null)}
            onSuccess={fetchFileStorageData}
          />
        )}
      </div>
    </div>
  );
};
